import type { EloRating } from "./types.js";

const DEFAULT_K = 32;

/** P(player beats question) when ratings are for a zero-sum "match". */
export function expectedPlayerScore(playerRating: EloRating, questionRating: EloRating): number {
  return 1 / (1 + 10 ** ((questionRating - playerRating) / 400));
}

export type EloUpdate = {
  player: EloRating;
  question: EloRating;
};

/**
 * One attempt: `correct` means the player "won" against the question's difficulty.
 * Question rating moves opposite the player to conserve relative information.
 */
export function updateAfterAttempt(
  playerRating: EloRating,
  questionRating: EloRating,
  correct: boolean,
  k: number = DEFAULT_K
): EloUpdate {
  const s = correct ? 1 : 0;
  const e = expectedPlayerScore(playerRating, questionRating);
  const delta = k * (s - e);
  return {
    player: roundRating(playerRating + delta),
    question: roundRating(questionRating - delta),
  };
}

/**
 * K scaled by how "new" a rating is (higher |rating - 1500| still uses same K here;
 * optional provisional K for first N games on caller side).
 */
export function newPlayerK(baseK: number = DEFAULT_K, gamesPlayed: number): number {
  if (gamesPlayed < 20) return baseK * 1.5;
  if (gamesPlayed < 50) return baseK * 1.25;
  return baseK;
}

function roundRating(r: number): EloRating {
  return Math.round(r * 10) / 10;
}
